
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthResponse } from '../../types/user';
import { APIHOST } from '../../common';
import AuthDialog from './AuthDialog';
import { useNavigate } from 'react-router';

interface User {
  id: number;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  userType?: string;
  domain?: string;
}

interface AuthContextType {
  currentUser: User | null;
  isLoggedIn: boolean;
  userType: string | null;
  showAuthDialog: (mode?: 'login' | 'register') => void;
  hideAuthDialog: () => void;
  login: (authResponse: AuthResponse) => void;
  logout: () => void;
  loadingUser: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authDialogMode, setAuthDialogMode] = useState<'login' | 'register'>('login');
  const [loadingUser, setLoadingUser] = useState(true);
  const navigate = useNavigate();

  // Verify user authentication status with API
  useEffect(() => {
    
    const verifyUser = async () => {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        console.debug('No auth token found, skipping user verification');
        setLoadingUser(false);
        return;
      }

      try {
    
        const response = await fetch(`${APIHOST}/api/users/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Authentication verification failed');
        }

        // Optional: Update user data if API returns updated info
        const userData = await response.json();
        if (userData) {
          // Update currentUser if needed
          setCurrentUser(userData);
          setLoadingUser(false);
          localStorage.setItem('user', JSON.stringify(userData));
        }
      } catch (error) {
        console.error('User verification failed:', error);
        // Clear auth data and set user to null
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        setCurrentUser(null);
        setLoadingUser(false);
      }
    };

   
      verifyUser();
    
  }, []);

  const login = (authResponse: AuthResponse) => {
    setCurrentUser(authResponse.user);
    localStorage.setItem('authToken', authResponse.jwt);
    localStorage.setItem('user', JSON.stringify(authResponse.user));
    setAuthDialogOpen(false);
    if (authResponse.user.userType === 'internal') {
      console.debug('Admin user logged in:', authResponse.user);
      navigate("/admin");
    } else {
      navigate("/");
    }
  };

  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setAuthDialogOpen(false);
    navigate("/");
  };

  const showAuthDialog = (mode: 'login' | 'register' = 'login') => {
    setAuthDialogMode(mode);
    setAuthDialogOpen(true);
  };

  const hideAuthDialog = () => {
    setAuthDialogOpen(false);
  };

  const isLoggedIn = !!currentUser;
  const userType = currentUser?.userType || null;

  const contextValue: AuthContextType = {
    currentUser,
    isLoggedIn,
    userType,
    showAuthDialog,
    hideAuthDialog,
    login,
    logout,
    loadingUser
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <AuthDialog
        open={authDialogOpen}
        onClose={hideAuthDialog}
        onAuthSuccess={login}
        initialMode={authDialogMode}
      />
    </AuthContext.Provider>
  );
};